import { DownloadOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Button, Statistic, Tooltip } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Customer } from '@/modules/customer/customer.schema'
import { Devlop } from '@/modules/devlop/devlop.schema'
import { Production } from '@/modules/production-project/production.schema'
import { ResourceType } from '@/schemas'

import { UserResourceDashboardContentCard, UserResourceDashboardLayout } from '../dashboard-layout'
import { DashboardPieChart } from '../user-resource-dashboard-chart/dashboard-chart-pie'
import { BigProjectDetailTable } from './big-project-detail-table'
import { BigProjectTrendDashboardCard } from './big-project-trend-dashboard'

export const BigProjectResourceDashboard: React.FC<{
  selfId: string
  resourceType: ResourceType
  resource: Devlop | Customer | Production
}> = ({ selfId, resourceType, resource }) => {
  const { t } = useTranslation()
  const ref = React.useRef<{ download: () => void }>(null)

  const startDate = new URLSearchParams(window.location.search).get('startDate')
  const endDate = new URLSearchParams(window.location.search).get('endDate')

  const initialData = {
    tops: [],
    count: 0,
    total: {
      percentage: 0,
      topTotalWorkload: 0,
      totalWorkload: 0,
      name: '',
    },
  }
  const {
    data: overviewData,
    error,
    run,
  } = useRequest<{
    tops: { selfId: string; name: string; workload: number; percentage: number }[]
    count: number
    total: {
      percentage: number
      name: string
      totalWorkload: number
      topTotalWorkload: number
    }
  }>(
    {
      url: '/api/work-resource/statistics/specific-project/general/infos',
      params: {
        startDate,
        endDate,
        selfId,
        resourceType,
      },
    },
    {
      manual: true,
      initialData,
    }
  )

  React.useEffect(() => {
    if (selfId && startDate && endDate) {
      run()
    }
  }, [selfId, startDate, endDate])
  const overview = error ? initialData : overviewData

  return (
    <UserResourceDashboardLayout>
      <UserResourceDashboardContentCard
        title={t('资源总览')}
        titleInfo={'占用该项目资源前六的普通或自定义项目总工时/该项目资源总工时'}
      >
        <div className='flex justify-around'>
          <div>
            <div className='font-medium text-center text-lg mb-4'>{t('资源占用前六的普通/自定义项目')}</div>
            <DashboardPieChart className='h-60 w-60' data={overview!.tops} total={overview!.total} />
          </div>
          <div className='flex flex-shrink-0 mt-11 ml-20 items-center'>
            <Statistic
              className='rounded-lg bg-gray-100 p-4 w-70'
              title={
                <span>
                  {t('关联普通项目数')}
                  <Tooltip title={'关联在该大项目下的普通/自定义项目数量'}>
                    <InfoCircleOutlined className='cursor-pointer text-sm ml-1' size={14} />
                  </Tooltip>
                </span>
              }
              value={overview!.count}
            />
          </div>
        </div>
      </UserResourceDashboardContentCard>
      <UserResourceDashboardContentCard title={t('资源趋势')}>
        <BigProjectTrendDashboardCard
          key='project'
          type='project'
          selfId={selfId}
          resourceType={resourceType}
          resource={resource}
        />
        <BigProjectTrendDashboardCard
          key='user'
          type='user'
          selfId={selfId}
          resourceType={resourceType}
          resource={resource}
        />
      </UserResourceDashboardContentCard>
      <UserResourceDashboardContentCard
        title={t('资源明细')}
        operations={
          <Tooltip title={t('下载')}>
            <Button onClick={() => ref.current?.download()} shape='circle' icon={<DownloadOutlined />} />
          </Tooltip>
        }
      >
        <BigProjectDetailTable ref={ref} selfId={selfId} resourceType={resourceType} />
      </UserResourceDashboardContentCard>
    </UserResourceDashboardLayout>
  )
}
